import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Button, message, Spin, Select } from "antd";
import { Trans } from "@lingui/macro";
import { FileExcelOutlined, CloseOutlined } from "@ant-design/icons";
import { useAxios } from "../../utils/hooks";
import ButtonWithIcon from "../../components/buttons/button-with-icon";

interface AutoCheckReportDrawerProps {
    visible: boolean;
    onClose: () => void;
}

const AutoCheckReportDrawer: React.FC<AutoCheckReportDrawerProps> = ({ visible, onClose }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [itemNumbers, setItemNumbers] = useState<string[]>([]);
    const [tarricCodes, setTarricCodes] = useState<string[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");

    useEffect(() => {
        if (visible) {
            loadSuppliers();
        } else {
            form.resetFields();
            setSelectedSupplier(null);
            setItemNumbers([]);
            setTarricCodes([]);
        }
    }, [visible]);

    const loadSuppliers = async () => {
        try {
            if (!axiosInstance.current) return;
            
            const response = await axiosInstance.current.get('/resource/suppliers');
            
            if (response && response.data) {
                const supplierList = Array.isArray(response.data) ? response.data : [];
                setSuppliers(supplierList);
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
        }
    };

    const loadItemNumbers = async (supplierCode: string) => {
        try {
            if (!axiosInstance.current) return;
            
            const response = await axiosInstance.current.get('/resource/items/item-numbers', {
                params: { supplierCode }
            });
            
            if (response && response.data) {
                setItemNumbers(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            console.error('Error loading item numbers:', error);
        }
    };

    const loadTarricCodes = async (supplierCode: string) => {
        try {
            if (!axiosInstance.current) return;
            
            const response = await axiosInstance.current.get('/resource/items/tarric-codes', {
                params: { supplierCode }
            });
            
            if (response && response.data) {
                setTarricCodes(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            console.error('Error loading tarric codes:', error);
        }
    };

    const handleSupplierChange = (supplierCode: string) => {
        setSelectedSupplier(supplierCode);
        form.setFieldsValue({ itemNo: undefined, tarricCode: undefined });
        setItemNumbers([]);
        setTarricCodes([]);
        
        if (supplierCode) {
            loadItemNumbers(supplierCode);
            loadTarricCodes(supplierCode);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const requestBody = {
                supplierCode: values.supplierCode,
                itemNo: values.itemNo || null,
                tarricCode: values.tarricCode || null
            };

            if (!axiosInstance.current) {
                throw new Error('Axios instance not initialized');
            }

            const response = await axiosInstance.current.post(
                '/resource/auto-checks/report',
                requestBody,
                {
                    responseType: 'blob',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response && response.data) {
                // Create blob URL and trigger download
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `autocheck_report_${values.supplierCode}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                message.success('Report downloaded successfully');
                onClose();
            }
        } catch (error) {
            const err = error as any;
            if (err.response && err.response.data) {
                // Try to parse error message from blob
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const errorObj = JSON.parse(reader.result as string);
                        message.error(errorObj.message || 'Failed to generate report');
                    } catch {
                        message.error('Failed to generate report');
                    }
                };
                reader.readAsText(err.response.data);
            } else if (err.message) {
                message.error(err.message);
            } else {
                message.error('Failed to generate report');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FileExcelOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
                    <span style={{ fontSize: '16px', fontWeight: '600' }}>
                        <Trans>Generate AutoCheck Report</Trans>
                    </span>
                </div>
            }
            placement="right"
            onClose={onClose}
            visible={visible}
            width={500}
            bodyStyle={{ paddingBottom: 80 }}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={onClose} style={{ marginRight: 8 }}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={loading}
                    >
                        <ButtonWithIcon icon={<FileExcelOutlined />} text={<Trans>Generate Report</Trans>} />
                    </Button>
                </div>
            }
        >
            <Spin spinning={loading}>
                <Form
                    form={form}
                    layout="vertical"
                    name="autocheck_report_form"
                >
                    <Form.Item
                        name="supplierCode"
                        label={<Trans>Supplier</Trans>}
                        rules={[
                            {
                                required: true,
                                message: <Trans>Please select supplier</Trans>
                            }
                        ]}
                    >
                        <Select
                            showSearch
                            placeholder="Select supplier"
                            optionFilterProp="children"
                            onChange={handleSupplierChange}
                            filterOption={(input, option) => {
                                const children = option && option.children ? option.children as unknown as string : '';
                                return children.toLowerCase().includes(input.toLowerCase());
                            }}
                        >
                            {suppliers.map((supplier) => (
                                <Select.Option key={supplier.code} value={supplier.code}>
                                    {supplier.code} - {supplier.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="itemNo"
                        label={<Trans>Item Number</Trans>}
                        help={<Trans>Optional - leave empty for all items</Trans>}
                    >
                        <Select
                            showSearch
                            placeholder="Select item number (optional)"
                            allowClear
                            disabled={!selectedSupplier}
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                                const children = option && option.children ? option.children as unknown as string : '';
                                return children.toLowerCase().includes(input.toLowerCase());
                            }}
                        >
                            {itemNumbers.map((itemNo) => (
                                <Select.Option key={itemNo} value={itemNo}>
                                    {itemNo}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="tarricCode"
                        label={<Trans>Tarric Code / CN Code</Trans>}
                        help={<Trans>Optional - leave empty for all tarric codes</Trans>}
                    >
                        <Select
                            showSearch
                            placeholder="Select tarric code (optional)"
                            allowClear
                            disabled={!selectedSupplier}
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                                const children = option && option.children ? option.children as unknown as string : '';
                                return children.toLowerCase().includes(input.toLowerCase());
                            }}
                        >
                            {tarricCodes.map((tarricCode) => (
                                <Select.Option key={tarricCode} value={tarricCode}>
                                    {tarricCode}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Spin>
        </Drawer>
    );
};

export default AutoCheckReportDrawer;
